import React, { useMemo, useState } from "react";
import {
  CalendarRange,
  ChevronDown,
  ChevronUp,
  Truck,
  Car,
  User,
  Clock,
  Scissors,
} from "lucide-react";

const daysOfWeek = {
  monday: "Segunda",
  tuesday: "Terça",
  wednesday: "Quarta",
  thursday: "Quinta",
  friday: "Sexta",
  saturday: "Sábado",
  sunday: "Domingo",
};

const styles = `
/* ——— Layout seguro (não vaza) ——— */
.sch-root { 
  box-sizing: border-box;
  position: relative;
  isolation: isolate;            /* cria contexto próprio */
  width: 100%;
  max-width: 100%;
  background: #ffffff;           /* fundo branco */
  color: #0f172a;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial;
}
.sch-root *, .sch-root *::before, .sch-root *::after { box-sizing: inherit; }

/* ——— Card principal ——— */
.sch-card { 
  width: 100%;
  max-width: 100%;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0,0,0,.06);
  overflow: hidden;              /* nada sai do card */
}

/* ——— Cabeçalho ——— */
.sch-header { 
  display:flex; align-items:center; justify-content:space-between;
  padding: 14px 16px; border-bottom:1px solid #eef2f7;
}
.sch-title { display:flex; align-items:center; gap:10px; font-weight:700; letter-spacing:.2px; }

/* ——— Botões ——— */
.sch-toggle, .sch-miniToggle {
  display:inline-flex; align-items:center; gap:8px;
  padding:8px 12px; border-radius:10px; cursor:pointer;
  border:1px solid #e5e7eb; background:#f8fafc; color:#0f172a; font-weight:600;
}
.sch-toggle:hover, .sch-miniToggle:hover { background:#f1f5f9; }

/* ——— Corpo ——— */
.sch-body { padding: 14px; display:flex; flex-direction:column; gap:16px; }

/* ——— Bloco por vendedor ——— */
.sch-seller { border:1px solid #eef2f7; border-radius:12px; padding:12px; background:#ffffff; }
.sch-sellerHead { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:10px; }
.sch-badge { background:#0ea5e9; color:#ffffff; padding:6px 10px; border-radius:999px; font-size:12px; font-weight:700; letter-spacing:.3px; text-transform:uppercase; }

/* ——— Grid de dias ——— */
.sch-grid { 
  display:grid; gap:12px; 
  grid-template-columns: repeat( auto-fit, minmax(240px, 1fr) ); 
  width: 100%;
}

/* ——— Card do dia ——— */
.sch-dayCard { 
  border:1px solid #e8edf3; border-radius:12px; padding:12px; background:#fbfdff;
  display:flex; flex-direction:column; gap:10px;
  min-width: 0;                 /* evita overflow em grids */
}
.sch-dayTop { display:flex; align-items:center; justify-content:space-between; gap:10px; }
.sch-dayName { font-weight:700; overflow-wrap:anywhere; }

/* ——— Chips ——— */
.sch-work { 
  display:inline-flex; align-items:center; gap:8px; font-size:12px; font-weight:700; 
  padding:4px 10px; border-radius:999px; border:1px solid;
}
.sch-work.good { background:#ecfdf5; color:#065f46; border-color:#a7f3d0; }
.sch-work.bad  { background:#fff1f2; color:#9f1239; border-color:#fecdd3; }

.sch-empty { color:#64748b; font-style:italic; border:1px dashed #e2e8f0; padding:10px; border-radius:10px; text-align:center; }

/* ——— Turno ——— */
.sch-shift { 
  display:flex; flex-direction:column; gap:8px; padding:10px; 
  border:1px solid #e8edf3; border-radius:10px; background:#ffffff;
}
.sch-row { display:flex; flex-wrap:wrap; gap:8px; color:#0f172a; }
.sch-kv { 
  display:inline-flex; align-items:center; gap:6px; 
  background:#f8fafc; border:1px solid #e5e7eb; padding:6px 10px; border-radius:999px; 
  font-size:12px; overflow-wrap:anywhere;
}
.sch-mono { font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Courier New", monospace; }

/* ——— Rodapé do dia ——— */
.sch-foot { margin-top:2px; font-size:12px; color:#64748b; text-align:right; }

/* ——— Estado vazio ——— */
.sch-emptyState { padding:24px; text-align:center; color:#64748b; }

/* ——— Garantias anti-overflow geral ——— */
.sch-root { word-break: break-word; overflow-wrap: anywhere; }
`;

const Shift = ({ d }) => {
  const from = d?.from || "-";
  const to = d?.to || "-";
  const cutoff = d?.cutoff || "-";
  const carrier = d?.carrier?.name || "-";
  const vehicleType = d?.vehicle?.vehicle_type || "-";
  const plate = d?.vehicle?.license_plate ? ` • ${d.vehicle.license_plate}` : "";
  const driver = d?.driver?.name || "-";
  const logistic = d?.logistic_type || "-";

  return (
    <div className="sch-shift">
      <div className="sch-row" title="Horário">
        <span className="sch-kv"><Clock size={14}/> <span className="sch-mono">{from}</span></span>
        <span className="sch-kv"><Clock size={14}/> <span className="sch-mono">{to}</span></span>
        <span className="sch-kv"><Scissors size={14}/> <span className="sch-mono">{cutoff}</span></span>
      </div>
      <div className="sch-row" title="Logística">
        <span className="sch-kv"><Truck size={14}/> {carrier}</span>
        <span className="sch-kv"><Car size={14}/> {vehicleType}{plate}</span>
        <span className="sch-kv"><User size={14}/> {driver}</span>
        <span className="sch-kv"><Truck size={14}/> {logistic}</span>
      </div>
    </div>
  );
};

const DayCard = ({ dayKey, entry }) => {
  const details = Array.isArray(entry?.detail) ? entry.detail : [];
  const work = !!entry?.work;
  return (
    <div className="sch-dayCard">
      <div className="sch-dayTop">
        <div className="sch-dayName">{daysOfWeek[dayKey] || dayKey}</div>
        <span className={`sch-work ${work ? "good" : "bad"}`}>{work ? "Trabalho" : "Folga"}</span>
      </div>

      {details.length === 0 ? (
        <div className="sch-empty">Nenhum período cadastrado</div>
      ) : (
        details.map((d, i) => <Shift key={i} d={d} />)
      )}

      <div className="sch-foot">Atualizado automaticamente</div>
    </div>
  );
};

const SellerBlock = ({ sellerId, schedule }) => {
  const [open, setOpen] = useState(true);
  const days = useMemo(() => Object.keys(schedule || {}), [schedule]);

  return (
    <section className="sch-seller">
      <div className="sch-sellerHead">
        <span className="sch-badge">Vendedor • {sellerId}</span>
        <button className="sch-miniToggle" onClick={() => setOpen(v => !v)}>
          {open ? <>Recolher <ChevronUp size={16} /></> : <>Expandir <ChevronDown size={16} /></>}
        </button>
      </div>

      {open && (
        <div className="sch-grid">
          {days.map((day) => (
            <DayCard key={day} dayKey={day} entry={schedule[day]} />
          ))}
        </div>
      )}
    </section>
  );
};

const ScheduleCard = ({ sellerData }) => {
  const [collapsed, setCollapsed] = useState(false);

  const normalized = useMemo(() => {
    if (Array.isArray(sellerData)) return sellerData;
    if (sellerData && typeof sellerData === "object") return [sellerData];
    return [];
  }, [sellerData]);

  if (normalized.length === 0) {
    return (
      <>
        <style>{styles}</style>
        <div className="sch-root">
          <div className="sch-card">
            <div className="sch-header">
              <div className="sch-title"><CalendarRange size={18}/> Horários de coleta</div>
            </div>
            <div className="sch-emptyState">Nenhum dado disponível no momento.</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="sch-root">
        <div className="sch-card">
          <div className="sch-header">
            <div className="sch-title"><CalendarRange size={18}/> Horários de coleta</div>
            <button
              className="sch-toggle"
              onClick={() => setCollapsed(v => !v)}
              aria-expanded={!collapsed}
              title={collapsed ? "Expandir" : "Recolher"}
            >
              {collapsed ? "Expandir" : "Recolher"}
              {collapsed ? <ChevronDown size={18}/> : <ChevronUp size={18}/>}
            </button>
          </div>

          {!collapsed && (
            <div className="sch-body">
              {normalized.map((seller, idx) => {
                const sellerId = Object.keys(seller)[0] ?? `#${idx+1}`;
                const schedule = seller[sellerId]?.schedule || {};
                return <SellerBlock key={sellerId} sellerId={sellerId} schedule={schedule} />;
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ScheduleCard;
